import type { DependencyList } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

export const SPLIT_KEY = ',';
export const COMBINATION_KEY = '+';
export const IS_MACOS = (
  (navigator as any)?.userAgentData?.platform ??
  navigator?.userAgent ??
  navigator?.platform ??
  ''
)
  .toLowerCase()
  .includes('mac');
// export const IS_MACOS = true;

export type Keys = string | string[] | ((opts: KeyOptions) => string | string[]);
export type Scopes = string | string[];
export type KeyboardModifiers = {
  alt?: boolean;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
  mod?: boolean;
};

export type Trigger = boolean | ((keyboardEvent: KeyboardEvent, hotkeysEvent: Hotkey) => boolean);

export type KeyOptions = {
  macos: boolean;
};

export type Options = {
  keys: Keys;
  action: HotkeyCallback;
  dependencies?: DependencyList;
  enabled?: boolean;
  /** is hotkey enabled on editable content? */
  enableOnContentEditable?: boolean;
  /** character to split the hotkey into (default: +) */
  combinationKey?: string;
  /** Character to separate different hotkeys. (Default: ,) */
  splitKey?: string;
  /** Listen to events on the document instead of the window. (Default: false) */
  document?: Document;
};

export type Hotkey = KeyboardModifiers & {
  keys?: string[];
  scopes?: Scopes;
  description?: string;
};

export type HotkeyCallback = (keyboardEvent: KeyboardEvent, hotkeysEvent: Hotkey) => void;

export const mappedKeys: Record<string, string> = {
  esc: 'escape',
  return: 'enter',
  '.': 'period',
  ',': 'comma',
  '-': 'slash',
  ' ': 'space',
  '`': 'backquote',
  '#': 'backslash',
  '+': 'bracketright',
  ShiftLeft: 'shift',
  ShiftRight: 'shift',
  AltLeft: 'alt',
  AltRight: 'alt',
  MetaLeft: 'meta',
  MetaRight: 'meta',
  OSLeft: 'meta',
  OSRight: 'meta',
  ControlLeft: 'ctrl',
  ControlRight: 'ctrl',
};
export const reservedModifierKeywords = ['shift', 'alt', 'meta', 'mod', 'ctrl'];

const useSafeLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const stopPropagation = (event: KeyboardEvent): void => {
  event.stopPropagation();
  event.preventDefault();
  event.stopImmediatePropagation();
};

const pressedKeys: Set<string> = new Set<string>();

const isHotkeyPressed = (key: string | string[], splitKey = ','): boolean => {
  const hotkeyArray = Array.isArray(key) ? key : key.split(splitKey);

  return hotkeyArray.every((hotkey) => pressedKeys.has(hotkey.trim().toLowerCase()));
};

const mapKey = (key: string): string => {
  return (mappedKeys[key] || key)
    .trim()
    .toLowerCase()
    .replace(/key|digit|numpad|arrow/, '');
};

const isHotkeyMatchingKeyboardEvent = (
  event: KeyboardEvent,
  hotkey: Hotkey,
  ignoreModifiers = false,
): boolean => {
  const { alt, meta, mod, shift, ctrl, keys } = hotkey;
  const { key: pressedKeyUppercase, code, ctrlKey, metaKey, shiftKey, altKey } = event;

  const keyCode = mapKey(code);
  const pressedKey = pressedKeyUppercase.toLowerCase();

  if (
    !keys?.includes(keyCode) &&
    !keys?.includes(pressedKey) &&
    !['ctrl', 'control', 'unknown', 'meta', 'alt', 'shift', 'os'].includes(keyCode)
  ) {
    return false;
  }

  if (!ignoreModifiers) {
    // We check the pressed keys for compatibility with the keyup event. In keyup events the modifier flags are not set.
    if (alt === !altKey && pressedKey !== 'alt') {
      return false;
    }

    if (shift === !shiftKey && pressedKey !== 'shift') {
      return false;
    }

    // Mod is a special key name that is checking for meta on macOS and ctrl on other platforms
    if (mod) {
      if (!metaKey && !ctrlKey) {
        return false;
      }
    } else {
      if (meta === !metaKey && pressedKey !== 'meta' && pressedKey !== 'os') {
        return false;
      }

      if (ctrl === !ctrlKey && pressedKey !== 'ctrl' && pressedKey !== 'control') {
        return false;
      }
    }
  }

  // All modifiers are correct, now check the key
  // If the key is set, we check for the key
  if (keys && keys.length === 1 && (keys.includes(pressedKey) || keys.includes(keyCode))) {
    return true;
  }

  if (keys) {
    // Check if all keys are present in pressedDownKeys set
    return isHotkeyPressed(keys);
  }

  if (!keys) {
    // If the key is not set, we only listen for modifiers, that check went alright, so we return true
    return true;
  }

  // There is nothing that matches.
  return false;
};

const parseInput = (input: Keys, splitKey = SPLIT_KEY): string[] => {
  const fromInput = typeof input === 'function' ? input({ macos: IS_MACOS }) : input;
  return (Array.isArray(fromInput) ? fromInput : fromInput.split(splitKey)).map((str) =>
    str.trim(),
  );
};

const parseHotkey = (hotkey: string, splitKey = '+'): Hotkey => {
  const keys = hotkey.toLowerCase().split(splitKey).map(mapKey);

  const modifiers: KeyboardModifiers = {
    alt: keys.includes('alt'),
    ctrl: keys.includes('ctrl') || keys.includes('control'),
    shift: keys.includes('shift'),
    meta: keys.includes('meta'),
    mod: keys.includes('mod'),
  };

  const singleCharKeys = keys.filter((k) => !reservedModifierKeywords.includes(k));

  return {
    ...modifiers,
    keys: singleCharKeys,
  };
};

export const getKeyLabel = (hotkey: string, combinationKey = COMBINATION_KEY): string[] => {
  return hotkey.split(combinationKey).map((key) => {
    const KEY = key.toUpperCase();
    switch (KEY) {
      case 'MOD':
        return 'Mod';
      case 'CTRL':
        return IS_MACOS ? '\u{2303}' : 'Ctrl';
      case 'ALT':
        return IS_MACOS ? '\u{2387}' : 'Alt';
      case 'META':
        return IS_MACOS ? '\u{2318}' : 'Meta';
      case 'SHIFT':
        return IS_MACOS ? '\u{21E7}' : 'Shift';
      default:
        return KEY;
    }
  });
};

export const useHotkeys = <T extends HTMLElement>(params: Options) => {
  const ref = useRef<T | null>(null);
  const keys = parseInput(params.keys);
  const { enabled = true } = params;

  const memoisedCallback = useCallback(params.action, params?.dependencies ?? []);
  // const callbackRef = useRef<HotkeyCallback>(memoisedCallback);

  useSafeLayoutEffect(() => {
    if (!enabled) return;
    const domNode: HTMLElement | Document = ref.current || document;

    const listener = (event: KeyboardEvent) => {
      if (ref.current !== null) {
        const rootNode = ref.current.getRootNode();
        if (
          (rootNode instanceof Document || rootNode instanceof ShadowRoot) &&
          rootNode.activeElement !== ref.current &&
          !ref.current.contains(rootNode.activeElement)
        ) {
          stopPropagation(event);
          return;
        }
      }

      if ((event.target as HTMLElement)?.isContentEditable && !params?.enableOnContentEditable) {
        return;
      }

      parseInput(keys).forEach((key) => {
        const hotkey = parseHotkey(key);

        if (isHotkeyMatchingKeyboardEvent(event, hotkey)) {
          stopPropagation(event);
          memoisedCallback(event, hotkey);
        }
      });
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (event.key === undefined) return;
      listener(event);
    };

    domNode.addEventListener('keydown', handleKeyUp as EventListener);

    return () => {
      domNode.removeEventListener('keydown', handleKeyUp as EventListener);
    };
  }, [...(params?.dependencies ?? []), enabled, keys]);

  return ref;
};
